import {AgendaService} from "crqlar-react-common/dist/services/agenda/agenda.service";
import {AgeCategoryService} from "crqlar-react-common/dist/services/ageCategory/ageCategory.service";
import {ContentCategoryService} from "crqlar-react-common/dist/services/contentCategory/contentCategory.service";
import {FormService} from "crqlar-react-common/dist/services/forms/form.service";
import {ClientUserService} from "crqlar-react-common/dist/services/user/clientUser.service";
import {PrintTemplateService} from "crqlar-react-common/dist/services/template/printTemplate.service";
import {PrintoutService} from "crqlar-react-common/dist/services/printout/printout.service";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import {ResourceCategoryService} from "crqlar-react-common/dist/services/resourceCategory/resourceCategory.service";
import {SectorService} from "crqlar-react-common/dist/services/sector/sector.service";
import {ServiceService} from "crqlar-react-common/dist/services/service/service.service";
import {ServiceCategoryService} from "crqlar-react-common/dist/services/serviceCategory/serviceCategory.service";
import {TaxRateService} from "crqlar-react-common/dist/services/rate/taxRate.service";
import {formatProfileListName} from "crqlar-react-common/dist/utils/profile.util";
import type {SettingsSearchFindOption, SettingsSearchProvider, SettingsSearchResult} from "./settingsSearch.types";
import type {SettingsNavigationId} from "../settingsNavigation";

const SEARCH_RESULT_LIMIT = 10

type SearchableSettingsEntity = {
    id?: string
    [key: string]: any
}

type SettingsEntitySearchOption<T extends SearchableSettingsEntity> = {
    id: string
    settingsId: SettingsNavigationId
    triggers: string[]
    group: string
    findLabel: string
    search: (term: string) => Promise<T[]>
    getLabel: (item: T) => string
    getPath: (item: T) => string
}

type CreateSettingsEntitySearchProvidersOptions = {
    organizationId?: string
    includeContentCategories: boolean
    canAccessSettingsItem?: (settingsId: SettingsNavigationId) => boolean
}

const normalizeTrigger = (value: string) => value.toLowerCase().replace(/\s+/g, ' ').trim()

const firstTenByName = () => ({
    offset: 0,
    limit: SEARCH_RESULT_LIMIT,
    sort: [{field: 'name', direction: 'ASC' as const}],
})

const firstTenByIdDesc = () => ({
    offset: 0,
    limit: SEARCH_RESULT_LIMIT,
    sort: [{field: 'id', direction: 'DESC' as const}],
})

const parseEntitySearchQuery = (query: string) => {
    const [triggerPart, ...termParts] = query.split(':')
    const trigger = normalizeTrigger(triggerPart || '')
    const term = termParts.join(':').trim()

    if (!trigger || !term) return null

    return {
        trigger,
        term,
    }
}

const createEntitySearchProvider = (
    options: SettingsEntitySearchOption<SearchableSettingsEntity>[]
): SettingsSearchProvider => async query => {
    const parsedQuery = parseEntitySearchQuery(query)
    if (!parsedQuery) return []

    const option = options.find(candidate => candidate.triggers.includes(parsedQuery.trigger))
    if (!option) return []

    const items = await option.search(parsedQuery.term)

    return items
        .filter(item => item.id)
        .slice(0, SEARCH_RESULT_LIMIT)
        .map<SettingsSearchResult>(item => ({
            id: `${option.id}-${item.id}`,
            section: 'result',
            label: option.getLabel(item) || '-',
            group: option.group,
            path: option.getPath(item),
            keywords: option.triggers,
        }))
}

const createSearchOptions = ({
    organizationId,
    includeContentCategories,
    canAccessSettingsItem,
}: CreateSettingsEntitySearchProvidersOptions): SettingsEntitySearchOption<SearchableSettingsEntity>[] => {
    const options: SettingsEntitySearchOption<SearchableSettingsEntity>[] = [
        {
            id: 'profile',
            settingsId: 'profiles',
            triggers: ['profile', 'profiles', 'guest', 'guests'],
            group: 'Profile',
            findLabel: 'profiles',
            search: async term => {
                const page = await ProfileService.getAllProfiles({
                    query: term,
                    limit: firstTenByName(),
                })

                return page.content || []
            },
            getLabel: profile => formatProfileListName({
                lastName: profile.lastName,
                firstName: profile.firstName,
                namePrefix: profile.namePrefix,
                academicTitle: profile.academicTitle,
            }),
            getPath: profile => `/system/profiles/${profile.id}`,
        },
        {
            id: 'resource',
            settingsId: 'resources',
            triggers: ['resource', 'resources', 'resouce', 'resouces'],
            group: 'Resource',
            findLabel: 'resources',
            search: async term => {
                const page = await ResourceService.getAllResources({
                    name: [term],
                    onlyRootResources: true,
                    limit: firstTenByName(),
                })

                return page.content || []
            },
            getLabel: resource => resource.name || '-',
            getPath: resource => `/system/resources/${resource.id}`,
        },
        {
            id: 'resourceCategory',
            settingsId: 'resourceCategories',
            triggers: ['resource cat', 'resource cats', 'resource category', 'resource categories'],
            group: 'Resource category',
            findLabel: 'resource categories',
            search: async term => {
                const page = await ResourceCategoryService.getAllResourceCategories({
                    name: [term],
                    limit: firstTenByName(),
                })

                return page.content || []
            },
            getLabel: category => category.name || '-',
            getPath: category => `/system/resourceCategories/${category.id}`,
        },
        {
            id: 'service',
            settingsId: 'services',
            triggers: ['service', 'services'],
            group: 'Service',
            findLabel: 'services',
            search: async term => {
                const page = await ServiceService.getAllServices({
                    name: [term],
                    limit: firstTenByName(),
                })

                return page.content || []
            },
            getLabel: service => service.name || '-',
            getPath: service => `/system/services/${service.id}`,
        },
        {
            id: 'serviceCategory',
            settingsId: 'serviceCategories',
            triggers: ['service cat', 'service cats', 'service category', 'service categories'],
            group: 'Service category',
            findLabel: 'service categories',
            search: async term => {
                const page = await ServiceCategoryService.getAllServiceCategories({
                    name: [term],
                    limit: firstTenByName(),
                })

                return page.content || []
            },
            getLabel: category => category.name || '-',
            getPath: category => `/system/serviceCategories/${category.id}`,
        },
        {
            id: 'taxRate',
            settingsId: 'taxRates',
            triggers: ['tax', 'taxes', 'tax rate', 'tax rates'],
            group: 'Tax rate',
            findLabel: 'tax rates',
            search: async term => {
                const page = await TaxRateService.getAllTaxRates({
                    name: [term],
                    limit: firstTenByName(),
                })

                return page.content || []
            },
            getLabel: taxRate => taxRate.name || '-',
            getPath: taxRate => `/system/taxRates/${taxRate.id}`,
        },
        {
            id: 'ageCategory',
            settingsId: 'ageCategories',
            triggers: ['age cat', 'age cats', 'age category', 'age categories'],
            group: 'Age category',
            findLabel: 'age categories',
            search: async term => {
                const page = await AgeCategoryService.getAllAgeCategories({
                    name: [term],
                    limit: firstTenByName(),
                })

                return page.content || []
            },
            getLabel: category => category.name || '-',
            getPath: category => `/system/ageCategories/${category.id}`,
        },
        {
            id: 'printTemplate',
            settingsId: 'printTemplates',
            triggers: ['print', 'print template', 'print templates'],
            group: 'Print template',
            findLabel: 'print templates',
            search: async term => {
                const page = await PrintTemplateService.getAllTemplates({
                    name: [term],
                    limit: firstTenByName(),
                })

                return page.content || []
            },
            getLabel: template => template.name || '-',
            getPath: template => `/system/printTemplates/${template.id}`,
        },
        {
            id: 'printout',
            settingsId: 'printouts',
            triggers: ['printout', 'printouts'],
            group: 'Printout',
            findLabel: 'printouts',
            search: async term => {
                const page = await PrintoutService.getAllPrintouts({
                    name: [term],
                    limit: firstTenByName(),
                })

                return page.content || []
            },
            getLabel: printout => printout.name || '-',
            getPath: printout => `/system/printouts/${printout.id}`,
        },
        {
            id: 'sector',
            settingsId: 'sectors',
            triggers: ['sector', 'sectors'],
            group: 'Sector',
            findLabel: 'sectors',
            search: async term => {
                const page = await SectorService.getAllSectors({
                    name: [term],
                    limit: firstTenByName(),
                })

                return page.content || []
            },
            getLabel: sector => sector.name || '-',
            getPath: sector => `/system/sectors/${sector.id}`,
        },
        {
            id: 'team',
            settingsId: 'team',
            triggers: ['team', 'user', 'users', 'team member', 'team members'],
            group: 'Team member',
            findLabel: 'team members',
            search: async term => {
                const page = await ClientUserService.getAllUsers({
                    query: term,
                    organizationId,
                    limit: firstTenByIdDesc(),
                })

                return page.content || []
            },
            getLabel: user => [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || '-',
            getPath: user => `/system/team/${user.id}`,
        },
        {
            id: 'agenda',
            settingsId: 'agenda',
            triggers: ['agenda', 'agendas'],
            group: 'Agenda',
            findLabel: 'agendas',
            search: async term => {
                const page = await AgendaService.getAllAgendas({
                    name: [term],
                    limit: firstTenByName(),
                })

                return page.content || []
            },
            getLabel: agenda => agenda.name || '-',
            getPath: agenda => `/system/agenda/${agenda.id}`,
        },
        {
            id: 'form',
            settingsId: 'forms',
            triggers: ['form', 'forms'],
            group: 'Form',
            findLabel: 'forms',
            search: async term => {
                const page = await FormService.listForms({
                    name: [term],
                    limit: firstTenByIdDesc(),
                })

                return page.content || []
            },
            getLabel: form => form.title || '-',
            getPath: form => `/system/forms/${form.id}`,
        },
    ]

    if (includeContentCategories) {
        options.push({
            id: 'contentCategory',
            settingsId: 'contentCategories',
            triggers: ['content cat', 'content cats', 'content category', 'content categories'],
            group: 'Content category',
            findLabel: 'content categories',
            search: async term => {
                const page = await ContentCategoryService.getAllContentCategories({
                    query: term,
                    limit: firstTenByName(),
                })

                return page.content || []
            },
            getLabel: category => category.name || '-',
            getPath: category => `/system/contentCategories/${category.id}`,
        })
    }

    return canAccessSettingsItem
        ? options.filter(option => canAccessSettingsItem(option.settingsId))
        : options
}

export const createSettingsEntitySearchProvider = (
    options: CreateSettingsEntitySearchProvidersOptions
): SettingsSearchProvider => createEntitySearchProvider(createSearchOptions(options))

export const createSettingsEntityFindOptions = (
    options: CreateSettingsEntitySearchProvidersOptions
): SettingsSearchFindOption[] => createSearchOptions(options).map(option => ({
    id: option.id,
    trigger: option.triggers[0],
    label: option.findLabel,
    group: option.group,
    keywords: option.triggers,
}))
